'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactNode } from 'react';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { AdvancedFilter } from '@morpho-org/ui-v6/client';
import { ChainIcon } from '@repo/web3';
import { xor } from 'lodash-es';
import { sortNetworkItemsByTvl } from '@/components/common/TableAdvancedFilters/sortNetworkItemsByTvl';
import { ChainConfig } from '@/config';
import { trackNetworkFilterCleared, trackNetworkFilterToggled } from '@/lib/analytics/client/trackers/network-filter';
const networkItems = ChainConfig.AVAILABLE_CHAIN_IDS.map(chainId => {
  const metadata = InterfaceChainUtils.getMetadata(chainId);
  return {
    id: String(chainId),
    label: metadata.chain.name,
    value: chainId,
    icon: <ChainIcon chainId={chainId} className="h-full w-full" />,
    tag: metadata.isNew ? 'New' : undefined
  };
});
interface AdvancedNetworkFilterProps {
  chainIdsFilter: ChainId[];
  handleChainIdsFilterChange: (chainIdsFilter: ChainId[]) => void;
  disabled: boolean;
  hasHighestZIndex?: boolean;
  onClear?: () => void;
  availableChains?: ChainId[];
  /** Per-chain total matching vault count from the API facet, shown next to each option. */
  chainCounts?: Map<ChainId, number>;
  /**
   * Absolute, filter-independent per-chain TVL (USD) from the API facet. Used to order the
   * options by chain size (largest first). Undefined until the API ships the field, in which
   * case a stable sort with all-equal keys preserves the static config order.
   */
  chainTvls?: Map<ChainId, number>;
  page?: string;
  resultLabel?: ReactNode;
}
const AdvancedNetworkFilter = t0 => {
  const $ = _c(26);
  const {
    chainIdsFilter,
    handleChainIdsFilterChange,
    disabled,
    hasHighestZIndex,
    onClear,
    availableChains,
    chainCounts,
    chainTvls,
    page,
    resultLabel
  } = t0;
  let t1;
  if ($[0] !== availableChains || $[1] !== chainCounts || $[2] !== chainIdsFilter || $[3] !== chainTvls) {
    let t2;
    if ($[5] !== availableChains) {
      t2 = network => !availableChains || availableChains.includes(network.value);
      $[5] = availableChains;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== chainCounts || $[8] !== chainIdsFilter) {
      t3 = network_0 => ({
        ...network_0,
        isChecked: chainIdsFilter.includes(network_0.value),
        count: chainCounts ? chainCounts.get(network_0.value) ?? 0 : undefined
      });
      $[7] = chainCounts;
      $[8] = chainIdsFilter;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t1 = sortNetworkItemsByTvl(networkItems.filter(t2).map(t3), chainTvls);
    $[0] = availableChains;
    $[1] = chainCounts;
    $[2] = chainIdsFilter;
    $[3] = chainTvls;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const items = t1;
  let t2;
  if ($[10] !== chainIdsFilter || $[11] !== handleChainIdsFilterChange || $[12] !== page) {
    t2 = value => {
      const newChainIdsFilter = xor(chainIdsFilter, [value]);
      handleChainIdsFilterChange(newChainIdsFilter);
      if (page) {
        trackNetworkFilterToggled({
          page,
          toggledChainId: value,
          nextChainIds: newChainIdsFilter
        });
      }
    };
    $[10] = chainIdsFilter;
    $[11] = handleChainIdsFilterChange;
    $[12] = page;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const toggleItem = t2;
  let t3;
  if ($[14] !== handleChainIdsFilterChange || $[15] !== onClear || $[16] !== page) {
    t3 = () => {
      handleChainIdsFilterChange([]);
      onClear?.();
      if (page) {
        trackNetworkFilterCleared({
          page
        });
      }
    };
    $[14] = handleChainIdsFilterChange;
    $[15] = onClear;
    $[16] = page;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const clear = t3;
  const t4 = chainIdsFilter.length > 0;
  let t5;
  if ($[18] !== clear || $[19] !== disabled || $[20] !== hasHighestZIndex || $[21] !== items || $[22] !== resultLabel || $[23] !== t4 || $[24] !== toggleItem) {
    t5 = <AdvancedFilter items={items} toggleItem={toggleItem} clear={clear} inputPlaceholder="Search for network" clearTooltipText="Remove Network filter" withSeparator={false} disabled={disabled} hasHighestZIndex={hasHighestZIndex} allLabel="Network" maxHeight="min(70vh, 600px)" resultLabel={resultLabel} hasSelection={t4} />;
    $[18] = clear;
    $[19] = disabled;
    $[20] = hasHighestZIndex;
    $[21] = items;
    $[22] = resultLabel;
    $[23] = t4;
    $[24] = toggleItem;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  return t5;
};
export default AdvancedNetworkFilter;